import { useState } from 'react';
import { ApiError } from '../api/client';
import { getRondoUserStats, getRondoPayoffStats, type RondoUserStats, type RondoPayoffStats, type PayoffBucket } from '../api/rondo';
import { useTabParam } from '../shell/useTabParam';
import { Card } from '../components/arc/card/card';
import { CompactSelect } from '../components/arc/compact-select/compact-select';
import { MetricCard } from '../components/arc/metric-card/metric-card';
import { PageState } from '../components/arc/page-state/page-state';
import { Progress } from '../components/arc/progress/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/arc/tabs/tabs';
import { useRondoPageData } from './useRondoPageData';
import './users-prehled.css';

type SubTab = 'zakladni' | 'aktivnich' | 'vyplaty';
type Period = 'd1' | 'd7' | 'd30' | 'd90';
const USERS_TABS: readonly SubTab[] = ['zakladni', 'aktivnich', 'vyplaty'];
const PERIODS: { value: Period; label: string }[] = [
  { value: 'd1', label: '1 den' }, { value: 'd7', label: '7 dní' },
  { value: 'd30', label: '30 dní' }, { value: 'd90', label: '90 dní' },
];
const COUNTRY_ORDER = ['CZ', 'SK', 'RO'];
const COUNTRY_NAME: Record<string, string> = { CZ: 'Česko', SK: 'Slovensko', RO: 'Rumunsko' };
const count = (value: number) => Number(value || 0).toLocaleString('cs-CZ');
const money = (value: number) => Number(value || 0).toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const currencySummary = (bucket: PayoffBucket) => Object.keys(bucket.byCurrency).map(currency => `${money(bucket.byCurrency[currency] ?? 0)} ${currency}`).join(' · ') || 'Bez částky';

export function UsersPrehledPage() {
  const [tab, setTab] = useTabParam<SubTab>('tab', USERS_TABS, 'zakladni');
  const [period, setPeriod] = useState<Period>('d30');
  const { data: stats, error, loading } = useRondoPageData<RondoUserStats>('users-stats', getRondoUserStats);
  const { data: payoffs, error: payoffsError, loading: payoffsLoading } = useRondoPageData<RondoPayoffStats>('payoffs-stats', getRondoPayoffStats);

  if (loading) return <PageState title="Načítám přehled uživatelů" loading />;
  if (error) return <PageState title="Přehled se nepodařilo načíst" message={error instanceof ApiError ? error.message : 'Načtení selhalo'} />;
  if (!stats) return null;

  return <section className="rondo-arc-users p1-arc-scope">
    <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">UŽIVATELÉ</div><h1>Přehled</h1><p>Aktivita podle posledního přihlášení, trhy a výplaty</p></div></header>
    <Tabs value={tab} onValueChange={value => setTab(value as SubTab)}>
      <div className="rondo-arc-users-toolbar">
        <TabsList aria-label="Přehled uživatelů">
          <TabsTrigger value="zakladni">Základní</TabsTrigger>
          <TabsTrigger value="aktivnich">Přehled aktivních</TabsTrigger>
          <TabsTrigger value="vyplaty">Výplaty</TabsTrigger>
        </TabsList>
        {tab === 'vyplaty' && <a className="rondo-arc-users-detail-link" href="/uzivatele/vyplaty">Detail výplat</a>}
      </div>
      <TabsContent value="zakladni"><BasicOverview stats={stats} period={period} setPeriod={setPeriod} /></TabsContent>
      <TabsContent value="aktivnich"><ActiveOverview stats={stats} /></TabsContent>
      <TabsContent value="vyplaty">{payoffs ? <PayoffsOverview payoffs={payoffs} /> : payoffsLoading ? <PageState title="Načítám výplaty" loading /> : <PageState title="Výplaty se nepodařilo načíst" message={payoffsError instanceof ApiError ? payoffsError.message : 'Načtení selhalo'} />}</TabsContent>
    </Tabs>
  </section>;
}

function BasicOverview({ stats, period, setPeriod }: { stats: RondoUserStats; period: Period; setPeriod: (value: Period) => void }) {
  const selectedLabel = PERIODS.find(item => item.value === period)?.label ?? '90 dní';
  const inactive = Math.max(0, stats.total - stats.active.d90);
  return <div className="rondo-arc-users-stack">
    <div className="rondo-arc-users-metrics">
      <MetricCard label={`Aktivní · ${selectedLabel}`} value={stats.active[period]} context="Podle posledního přihlášení" />
      <MetricCard label="Registrovaní celkem" value={stats.total} context="Všechny trhy" />
      <MetricCard label="Aktivní za 90 dní" value={stats.active.d90} context={`${stats.total ? Math.round(stats.active.d90 / stats.total * 100) : 0} % všech uživatelů`} />
      <MetricCard label="Mimo 90 dní" value={inactive} context="Bez přihlášení v období" />
    </div>
    <Card title="Aktivita podle trhu" description="Registrace a poslední přihlášení" className="rondo-arc-users-card">
      <div className="rondo-arc-users-card-controls"><span>Vybrané období</span><CompactSelect value={period} options={PERIODS} onChange={value => setPeriod(value as Period)} ariaLabel="Vybrané období aktivity" /></div>
      <div className="rondo-arc-users-table-wrap"><table className="rondo-arc-users-table">
        <thead><tr><th>Trh</th><th>Aktivní · {selectedLabel}</th><th>Aktivní 7 dní</th><th>Aktivní 90 dní</th><th>Registrace 30 dní</th><th>Mimo 90 dní</th></tr></thead>
        <tbody>{stats.byCountry.map(country => <tr key={country.country}>
          <th scope="row"><span className="rondo-arc-users-market"><span className="rondo-arc-users-market-mark">{country.country}</span><span><strong>{COUNTRY_NAME[country.country] ?? country.country}</strong><small>{country.currency}</small></span></span></th>
          <td>{count(period === 'd1' ? country.active1d : period === 'd7' ? country.active7d : period === 'd30' ? country.active30d : country.active90d)}</td>
          <td>{count(country.active7d)}</td><td>{count(country.active90d)}</td><td>{count(country.registrace30d)}</td><td>{count(Math.max(0, country.total - country.active90d))}</td>
        </tr>)}</tbody>
      </table></div>
    </Card>
  </div>;
}

function ActiveOverview({ stats }: { stats: RondoUserStats }) {
  return <div className="rondo-arc-users-stack">
    <div className="rondo-arc-users-metrics">{PERIODS.map(period => <MetricCard key={period.value} label={`Aktivní · ${period.label}`} value={stats.active[period.value]} context="Všechny trhy · kumulativně" />)}</div>
    <Card title="Aktivita podle trhu" description="Podíl trhu na aktivních uživatelích za 30 dní" className="rondo-arc-users-card">
      <div className="rondo-arc-users-activity-list">{stats.byCountry.map(country => <div className="rondo-arc-users-activity" key={country.country}>
        <div className="rondo-arc-users-activity-head"><span className="rondo-arc-users-market"><span className="rondo-arc-users-market-mark">{country.country}</span><span><strong>{COUNTRY_NAME[country.country] ?? country.country}</strong><small>{country.currency}</small></span></span><strong>{count(country.active30d)}</strong></div>
        <Progress value={country.active30d} max={stats.active.d30 || 1} label={`${country.country} · podíl z aktivních za 30 dní`} />
        <dl><div><dt>Registrace</dt><dd>{count(country.registrace30d)}</dd></div><div><dt>Spin hráči</dt><dd>{count(country.spinUsers)}</dd></div><div><dt>Wallet IN</dt><dd>{money(country.walletIn)} {country.currency}</dd></div><div><dt>Wallet OUT</dt><dd>{money(country.walletOut)} {country.currency}</dd></div></dl>
      </div>)}</div>
    </Card>
  </div>;
}

function PayoffsOverview({ payoffs }: { payoffs: RondoPayoffStats }) {
  return <div className="rondo-arc-users-stack">
    <div className="rondo-arc-users-metrics">
      <MetricCard label="Dnešní dávka" value={payoffs.todayBatch.count} context={currencySummary(payoffs.todayBatch)} />
      <MetricCard label="Ve frontě" value={payoffs.requests?.count ?? 0} context="Před zpracováním" />
      <MetricCard label="Čekající" value={payoffs.pending.count} context={currencySummary(payoffs.pending)} />
      <MetricCard label="Doručené" value={payoffs.delivered.count} context={`Za ${payoffs.delivered.windowDays} dní`} />
    </div>
    <div className="rondo-arc-users-two-col">
      <Card title="Stav žádostí" description="Od přijetí po doručení nebo vrácení" className="rondo-arc-users-card">
        <div className="rondo-arc-users-status-list">
          <StatusRow label="Nové" bucket={payoffs.new} />
          <StatusRow label="Čekající" bucket={payoffs.pending} />
          <StatusRow label="Pozdržené" bucket={payoffs.held} />
          <StatusRow label="Doručené" bucket={payoffs.delivered} />
          <StatusRow label="Selhalo" bucket={payoffs.failed} />
        </div>
      </Card>
      <Card title="Výplaty podle trhu" description="Počet žádostí v každém stavu" className="rondo-arc-users-card">
        <div className="rondo-arc-users-table-wrap"><table className="rondo-arc-users-table">
          <thead><tr><th>Trh</th><th>Nové</th><th>Čekající</th><th>Pozdržené</th><th>Doručené</th><th>Selhalo</th></tr></thead>
          <tbody>{COUNTRY_ORDER.map(code => { const row = payoffs.byCountry[code]; return row ? <tr key={code}><th scope="row">{COUNTRY_NAME[code]}</th><td>{count(row.new)}</td><td>{count(row.pending)}</td><td>{count(row.held)}</td><td>{count(row.delivered)}</td><td>{count(row.failed)}</td></tr> : null; })}</tbody>
        </table></div>
      </Card>
    </div>
  </div>;
}

function StatusRow({ label, bucket }: { label: string; bucket: PayoffBucket }) {
  return <div><span>{label}</span><strong>{count(bucket.count)}</strong><small>{currencySummary(bucket)}</small></div>;
}
